import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Alert, AuthShell, Badge, Button, Divider, Link, Stack, TextField, Typography, } from '@platform/design-system';
import { useSession } from "../auth/session.js";
import { describeError } from "../http/errors.js";
import { RouterLink } from "../router/router-link.js";
import { CodeStep, ConfirmEmailStep, NewPasswordStep } from "./auth-steps.js";
import { emailSchema } from "./password-rules.js";
import { TotpSetupPanel } from "./totp-setup-panel.js";
/** The `AuthShell` props an `AuthFrame` sets. */
export function authShellFrame(frame) {
    return {
        brandName: frame.brandName,
        pitch: frame.pitch,
        ...(frame.supporting ? { supporting: frame.supporting } : {}),
    };
}
const credentialsForm = z.object({
    email: emailSchema,
    password: z.string().min(1, 'Enter your password'),
});
/**
 * Sign-in over SRP with every challenge inline (onboarding spec: "the Login screen must handle
 * NEW_PASSWORD_REQUIRED inline"): credentials → set a permanent password (invitation) →
 * authenticator code, or authenticator enrolment when the pool requires one. An invitation
 * link signs in with its temporary password by itself, so the person only ever types the
 * password they choose. A personal product's person who never entered the emailed sign-up code
 * confirms it here and is signed in with the password they just typed (held in memory for that
 * one step). Mock mode adds the fixture buttons above the same form.
 */
export function SignInScreen(props) {
    const { frame, arrival, onSignedIn } = props;
    const session = useSession();
    const [step, setStep] = useState({ kind: 'credentials' });
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(null);
    const tenantHint = arrival?.tenant ?? undefined;
    const invitation = Boolean(arrival?.temporaryPassword);
    /** The credentials of the sign-in in progress, only to sign in again after the email code. */
    const credentials = useRef(null);
    const advance = useCallback((result) => {
        setError(null);
        switch (result.kind) {
            case 'done':
                credentials.current = null;
                onSignedIn(result.principal);
                return;
            case 'confirm-sign-up':
                setStep({ kind: 'confirm-email', email: credentials.current?.email ?? '' });
                return;
            case 'new-password-required':
                setStep({ kind: 'new-password' });
                return;
            case 'totp-code':
                setStep({ kind: 'totp-code' });
                return;
            case 'totp-setup':
                setStep({ kind: 'totp-setup', secret: result.secret, uri: result.uri });
                return;
        }
    }, [onSignedIn]);
    const run = useCallback(async (id, action) => {
        setBusy(id);
        setError(null);
        try {
            advance(await action());
        }
        catch (e) {
            setError(describeError(e, 'Sign-in failed. Try again.'));
        }
        finally {
            setBusy(null);
        }
    }, [advance]);
    // An invitation link: sign in with the temporary password without asking for it.
    const autoStarted = useRef(false);
    useEffect(() => {
        if (autoStarted.current || !arrival?.temporaryPassword || !arrival.email)
            return;
        autoStarted.current = true;
        const { email, temporaryPassword } = arrival;
        setBusy('invitation');
        session
            .signIn({ email, password: temporaryPassword, ...(tenantHint ? { tenantHint } : {}) })
            .then(advance)
            .catch(() => {
            setStep({ kind: 'credentials', invitationExpired: true });
        })
            .finally(() => setBusy(null));
    }, [arrival, session, tenantHint, advance]);
    const title = step.kind === 'confirm-email'
        ? 'Confirm your email'
        : step.kind === 'new-password'
            ? 'Set your password'
            : step.kind === 'totp-code'
                ? 'Two-step verification'
                : step.kind === 'totp-setup'
                    ? 'Set up two-step verification'
                    : busy === 'invitation'
                        ? 'Opening your invitation'
                        : 'Sign in';
    const lead = step.kind === 'confirm-email'
        ? `Enter the code we emailed to ${step.email} when you signed up.`
        : step.kind === 'new-password'
            ? 'Choose the password you will sign in with from now on.'
            : step.kind === 'totp-code'
                ? 'Enter the code from your authenticator app.'
                : step.kind === 'totp-setup'
                    ? 'This account requires an authenticator app. Set one up to continue.'
                    : props.workspaceLabel
                        ? `Continue to ${props.workspaceLabel}.`
                        : session.mode === 'mock'
                            ? 'Choose who to sign in as, or use an email and password.'
                            : 'Continue with your email and password.';
    return (_jsx(AuthShell, { ...authShellFrame(frame), title: title, lead: lead, children: _jsxs(Stack, { spacing: 2, children: [error && _jsx(Alert, { severity: "error", children: error }), step.kind === 'credentials' && props.notice && (_jsx(Alert, { severity: "success", role: "status", children: props.notice })), step.kind === 'credentials' && step.invitationExpired && (_jsx(Alert, { severity: "warning", children: "This invitation link no longer works \u2014 it may have expired. Sign in if you already have a password, or ask a workspace admin to send a new invitation." })), step.kind === 'credentials' && busy === 'invitation' && (_jsx(Typography, { variant: "body2", color: "text.secondary", role: "status", children: "Signing you in with your invitation\u2026" })), step.kind === 'credentials' && busy !== 'invitation' && (_jsx(CredentialsStep, { ...props, busy: busy, invitation: invitation, onSubmit: (v) => {
                        credentials.current = { email: v.email, password: v.password };
                        void run('credentials', () => session.signIn({ ...v, ...(tenantHint ? { tenantHint } : {}) }));
                    }, onFixture: (id) => void run(id, () => session.signInAsFixture(id)) })), step.kind === 'confirm-email' && (_jsx(ConfirmEmailStep, { email: step.email, submitLabel: "Confirm and sign in", busy: busy === 'confirm-email', onConfirmed: () => {
                        const c = credentials.current;
                        if (!c) {
                            setStep({ kind: 'credentials' });
                            return;
                        }
                        void run('confirm-email', () => session.signIn({ ...c, ...(tenantHint ? { tenantHint } : {}) }));
                    } })), step.kind === 'new-password' && (_jsx(NewPasswordStep, { busy: busy === 'new-password', onSubmit: (v) => void run('new-password', () => session.completeNewPassword(v.password)) })), step.kind === 'totp-code' && (_jsx(CodeStep, { busy: busy === 'totp', label: "Authenticator code", submitLabel: "Verify", onSubmit: (v) => void run('totp', () => session.confirmTotp(v.code)) })), step.kind === 'totp-setup' && (_jsxs(Stack, { spacing: 2.5, children: [_jsx(TotpSetupPanel, { secret: step.secret, uri: step.uri }), _jsx(CodeStep, { busy: busy === 'totp-setup', label: "Code from the app", submitLabel: "Turn on two-step verification", onSubmit: (v) => void run('totp-setup', () => session.confirmTotpSetup(v.code)) })] })), step.kind !== 'credentials' && (_jsx(Button, { variant: "text", size: "small", onClick: () => {
                        setError(null);
                        setStep({ kind: 'credentials' });
                    }, sx: { alignSelf: 'flex-start' }, children: "Start over" }))] }) }));
}
function CredentialsStep({ arrival, forgotPasswordHref, signUpHref, signUpLabel, busy, invitation, onSubmit, onFixture, }) {
    const { mode, fixtures } = useSession();
    const form = useForm({
        resolver: zodResolver(credentialsForm),
        defaultValues: { email: arrival?.email ?? '', password: '' },
    });
    const submit = form.handleSubmit(onSubmit);
    return (_jsxs(Stack, { spacing: 2.5, children: [mode === 'mock' && (_jsxs(Stack, { spacing: 1.5, children: [_jsx(Badge, { tone: "warning", label: "Mock session \u2014 no backend" }), fixtures.map((f) => (_jsx(Button, { size: "large", busy: busy === f.id, busyLabel: "Signing in\u2026", disabled: busy !== null && busy !== f.id, onClick: () => onFixture(f.id), sx: { justifyContent: 'flex-start', height: 'auto', py: 1.25, textAlign: 'left' }, children: _jsxs(Stack, { spacing: 0.25, sx: { alignItems: 'flex-start' }, children: [_jsx(Typography, { variant: "subtitle2", component: "span", children: f.label }), _jsx(Typography, { variant: "caption", color: "text.secondary", component: "span", sx: { whiteSpace: 'normal' }, children: f.description })] }) }, f.id))), _jsx(Divider, { children: _jsx(Typography, { variant: "caption", color: "text.tertiary", children: "or with email and password" }) })] })), _jsx("form", { onSubmit: (e) => void submit(e), noValidate: true, children: _jsxs(Stack, { spacing: 2, children: [_jsx(TextField, { label: "Email", type: "email", autoComplete: "username", ...form.register('email'), error: form.formState.errors.email?.message }), _jsx(TextField, { label: invitation ? 'Temporary password' : 'Password', type: "password", autoComplete: invitation ? 'one-time-code' : 'current-password', ...form.register('password'), error: form.formState.errors.password?.message, ...(invitation ? { hint: 'From the invitation email.' } : {}) }), _jsx(Button, { type: "submit", variant: "contained", busy: busy === 'credentials', busyLabel: "Signing in\u2026", disabled: busy !== null && busy !== 'credentials', children: "Sign in" })] }) }), (forgotPasswordHref || signUpHref) && (_jsxs(Stack, { direction: "row", spacing: 2, sx: { justifyContent: 'space-between', flexWrap: 'wrap' }, children: [forgotPasswordHref ? (_jsx(Link, { component: RouterLink, href: forgotPasswordHref, variant: "body2", children: "Forgot password?" })) : (_jsx("span", {})), signUpHref && (_jsx(Link, { component: RouterLink, href: signUpHref, variant: "body2", children: signUpLabel ?? 'Create a workspace' }))] }))] }));
}
//# sourceMappingURL=sign-in-screen.js.map